import type { ReactNode } from 'react';
import React, { forwardRef, useState } from 'react';

import { ButtonBase, Menu, MenuItem, Typography } from '@mui/material';
import { Circle } from 'lucide-react';
import { Apollo } from 'src/gql/generated';
import type { ReminderType } from 'src/gql/generated/apollo-types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  button: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
  },
}));

interface ChooseReminderTypeButtonProps {
  children: ReactNode;
  onClick: (e: React.MouseEvent) => void;
}
export const ChooseReminderTypeButton = forwardRef(function AddAssigneeButton(
  { children, onClick }: ChooseReminderTypeButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const { classes } = useStyles();

  return (
    <ButtonBase ref={ref} className={classes.button} onClick={onClick}>
      {children}
    </ButtonBase>
  );
});

interface ReminderTypePickerProps {
  onTypeChange: (type: Apollo.ReminderType) => void;
  type: Apollo.ReminderType;
}

export function ReminderTypePicker({ onTypeChange, type }: ReminderTypePickerProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <ChooseReminderTypeButton ref={setAnchorEl} onClick={() => setIsMenuOpen(!isMenuOpen)}>
        <Circle color={getReminderTypeColor(type)} fill={getReminderTypeColor(type)} size={12} />
        <Typography>{type}</Typography>
      </ChooseReminderTypeButton>
      <ReminderTypeMenu
        anchorEl={anchorEl}
        open={isMenuOpen}
        selectedType={type}
        onClick={(_event, value) => onTypeChange(value)}
        onClose={() => setIsMenuOpen(false)}
      />
    </>
  );
}

const useMenuStyles = makeStyles()(theme => ({
  menuItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

interface ReminderTypeMenuProps {
  anchorEl: HTMLButtonElement | null;
  onClick: (event: React.ChangeEvent<unknown>, value: ReminderType) => void;
  onClose: () => void;
  open: boolean;
  selectedType: ReminderType;
}

export const orderedReminderTypes: Apollo.ReminderType[] = [
  Apollo.ReminderType.Expiration,
  Apollo.ReminderType.Milestone,
  Apollo.ReminderType.Obligation,
  Apollo.ReminderType.Renewal,
];

function ReminderTypeMenu({
  anchorEl,
  onClick,
  onClose,
  open,
  selectedType,
}: ReminderTypeMenuProps) {
  const { classes } = useMenuStyles();

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      open={open}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      onClose={onClose}
    >
      {orderedReminderTypes.map((type, index) => {
        return (
          <MenuItem
            key={index}
            className={classes.menuItem}
            selected={selectedType === type}
            onClick={event => {
              onClose();
              onClick(event, type);
            }}
          >
            <Circle
              color={getReminderTypeColor(type)}
              fill={getReminderTypeColor(type)}
              size={12}
            />
            <Typography>{type}</Typography>
          </MenuItem>
        );
      })}
    </Menu>
  );
}

enum ReminderTypeColor {
  Expiration = '#00870E',
  Milestone = '#833FCC',
  Obligation = '#F9CB40',
  Renewal = '#3E88EC',
}

export function getReminderTypeColor(type: Apollo.ReminderType) {
  switch (type) {
    case Apollo.ReminderType.Expiration:
      return ReminderTypeColor.Expiration;
    case Apollo.ReminderType.Milestone:
      return ReminderTypeColor.Milestone;
    case Apollo.ReminderType.Obligation:
      return ReminderTypeColor.Obligation;
    case Apollo.ReminderType.Renewal:
      return ReminderTypeColor.Renewal;
  }
}
